'use client';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import classNames from 'classnames';
import { useState, useCallback, useMemo } from 'react';
import { BrandId } from '../../utils/override';
import Button from './Button';
import {
  getLoanSearchParamsWithDefaultComponentParam,
  LoanSearchParams,
} from './cms/helpers/loanSearchParams';
import { pushDataLayer } from './cms/pushDataLayer';
import { TRANSLATIONS } from './cms/translations';
import { FilterInput } from './filterInput';
import ArrowRightAlt from './icons/ArrowRightAlt';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

interface PersonalLoanFormProps {
  title: string;
  brand: BrandId;
}

export const PersonalLoanForm = ({ title, brand }: PersonalLoanFormProps) => {
  const defaults = getLoanSearchParamsWithDefaultComponentParam(
    brand,
    undefined,
    undefined,
  );

  const [loanFilters, setLoanFilters] = useState({
    amount: defaults.amount,
    tenure: defaults.tenure,
  });
  const minTenure = 6;
  const minAmount = 1000;
  const ssMaxAmount = 100000;
  const mhMaxAmount = 600000;
  const maxTenure = '84';

  const context = useClientContext();
  const currencyPrefix = context.currencyDisplay;
  const defaultParams: LoanSearchParams = {
    amount: defaults.amount.toString(),
    tenure: defaults.tenure.toString(),
  };
  const vertical = 'personal-loan';
  const verticalUrl = '/personal-loan';
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const [amountError, setAmountError] = useState<string | null>(null);
  const [tenureError, setTenureError] = useState<string | null>(null);

  const handleFinished = (params: LoanSearchParams, isError?: boolean) => {
    if (params.amount !== undefined) {
      setAmountError(isError ? 'Invalid amount' : null);
      if (!isError) {
        setLoanFilters((prev) => ({
          ...prev,
          amount: params.amount,
        }));
      }
    }
    if (params.tenure !== undefined) {
      setTenureError(isError ? 'Invalid tenure' : null);
      if (!isError) {
        setLoanFilters((prev) => ({
          ...prev,
          tenure: params.tenure,
        }));
      }
    }
  };

  const isAmountValid = useMemo(() => {
    const amount = Number(loanFilters.amount);
    if (isNaN(amount)) {
      return false;
    }

    return amount >= minAmount && !amountError;
  }, [loanFilters.amount, amountError]);

  const isTenureValid = useMemo(() => {
    const tenure = Number(loanFilters.tenure);
    if (isNaN(tenure)) {
      return false;
    }

    return tenure >= minTenure && !tenureError;
  }, [loanFilters.tenure, tenureError]);

  const isFormValid = isAmountValid && isTenureValid;

  const handleCompare = useCallback(() => {
    if (!isFormValid) return;

    const params = new URLSearchParams({
      amount: loanFilters.amount.toString(),
      tenure: loanFilters.tenure.toString(),
    });

    pushDataLayer({
      event: 'final_search',
      search_result_parameter: `amount-${loanFilters.amount}|tenure-${loanFilters.tenure}`,
      vertical_code: vertical,
      component_type: 'bottom-sticky',
      component_title: `${title}/Search CTA_Compare Now`,
      action: 'click',
    });

    window.open(`${verticalUrl}?${params.toString()}#pl-input`, '_blank');
  }, [isFormValid, loanFilters, title, vertical]);

  return (
    <div className="flex w-full flex-col gap-spacing/gap/y">
      <div className="grid grid-cols-1 gap-spacing/gap/x md:grid-cols-2">
        <div className="flex-1">
          <FilterInput
            title={tr('personal_loan_amount', staticText, {
              currency: context.currencyDisplay,
            })}
            help={tr('personal_loan_amount_help', staticText, {
              currency: context.currencyDisplay,
              min_amount: minAmount.toString(),
              max_amount: (context.brand === 'singsaver'
                ? ssMaxAmount
                : mhMaxAmount
              ).toString(),
            })}
            defaultValue={defaultParams}
            prefix={currencyPrefix}
            onFinished={handleFinished}
            searchParam="amount"
            min={minAmount}
          />
        </div>
        <div className="flex-1">
          <FilterInput
            defaultValue={defaultParams}
            title={staticText.personal_loan_tenure}
            help={tr('personal_loan_tenure_help', staticText, {
              currency: context.currencyDisplay,
              min_tenure: minTenure.toString(),
              max_tenure: maxTenure,
            })}
            min={minTenure}
            searchParam="tenure"
            onFinished={handleFinished}
          />
        </div>
      </div>
      <div className="flex w-full items-center justify-between pt-spacing/gap/y">
        <p className="mhg-alpha-typography-body-2 hidden text-text/secondary-lighter md:inline">
          {staticText.redirect_after_clicking}.
        </p>
        <Button
          styleType="primary"
          onClick={handleCompare}
          endIcon={<ArrowRightAlt className="size-shapes/icon-size-md" />}
          disabled={!isFormValid}
          className={classNames(
            'flex w-full items-center justify-center transition-colors duration-200 md:w-auto',
            {
              'cursor-not-allowed bg-gray-300 text-gray-500': !isFormValid,
            },
          )}
        >
          <span>{staticText.compare_now}</span>
        </Button>
      </div>
    </div>
  );
};
